import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Box,
    Checkbox,
    HStack,
    Input,
    Link,
    Menu,
    VStack,
    Text,
    Spinner,
    IconButton,
    Select,
    Popover,
    Portal,
    Skeleton,
    SkeletonCircle,
    createListCollection,
} from "@chakra-ui/react";
import { HiOutlineUserGroup, HiArrowUp, HiArrowDown, HiStar, HiOutlineStar, HiEllipsisVertical, HiOutlineMagnifyingGlass, HiChevronDown, HiOutlineChatBubbleLeft, HiOutlineChevronLeft } from "react-icons/hi2";
import {
    formatMetadataDisplay,
    getTimelineBadgeProps,
    getTimelineEventDescription,
    parseMetadata,
} from "./components/candidate/timelineHelpers";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { ConversationService } from "@/services/ConversationService";
import { ModelService } from "@/services/ModelService";
import { fromApiResponse } from "@/services/jobOfferMapper";
import JobOfferFormModal from "@/components/jobOffer/JobOfferFormModal";
import { CandidateService } from "@/services/CandidateService";
import InfoField from "./components/InfoField";
import MobileFiltersPanel from "./components/MobileFiltersPanel";
import DateInput from "./components/DateInput";
import BulkActionsBar from "./components/BulkActionsBar";
import BulkChangeStatusModal from "./components/candidate/BulkChangeStatusModal";
import BulkEmailModal from "./components/candidate/BulkEmailModal";
import { toaster } from "@/components/ui/toaster";
import useCandidateSingleActions from "./hooks/useCandidateSingleActions";
import dayjs from "dayjs";
import { AXES } from "./components/CandidateAxisPill";
import { getScoreBucket } from "@/scoringUi";
import { useRecruitmentScoring } from "@/auth/ScoringContext";
import useScoreLabels from "./components/candidate/useScoreLabels";
import useOfferPreparationPolling from "@/components/hooks/useOfferPreparationPolling";
import {isOfferPreparing, isOfferPreparationFailed, isOfferLinkLocked, offerLinkLockedKey} from "@/utils/offerPreparation";
import {RecruitmentStatusBadge} from "@/pages/models/RecruitmentStatusBadge";

/** Converts a CandidateEvaluationSummaryResponse (PascalCase status from the API) into the shape getScoreBucket expects. */
const toEvaluationBucketShape = (evaluation) =>
    evaluation ? { scorePercent: evaluation.scorePercent, status: evaluation.status?.toUpperCase(), scoreRaw: evaluation.scoreRaw, scoreMax: evaluation.scoreMax } : null;

const AVATAR_PALETTE = [
    { bg: "#EDE4FB", color: "#6D28D9" },
    { bg: "#DFF3EE", color: "#0F766E" },
    { bg: "#FDEFDC", color: "#B45309" },
    { bg: "#FCE4E9", color: "#BE123C" },
    { bg: "#E1EEFD", color: "#1D4ED8" },
];

const avatarColorsFor = (seed) => AVATAR_PALETTE[Math.abs(hashCode(seed)) % AVATAR_PALETTE.length];
const hashCode = (str) => (str ?? "").split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
const initialsOf = (first, last) => `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";

const STATUS_STYLES = {
    initialized: { bg: "#EAEAF2", color: "#65616F" },
    created: { bg: "#E1EEFD", color: "#1D4ED8" },
    finalized: { bg: "#FDEFDC", color: "#B45309" },
    qualifiedfornextstage: { bg: "#F1E8FE", color: "#6D28D9" },
    furtherselection: { bg: "#E1F7FA", color: "#0E7C86" },
    readytohire: { bg: "#E1F5F1", color: "#0F766E" },
    hired: { bg: "#E4F7EC", color: "#178A4C" },
    rejected: { bg: "#FCE8EC", color: "#C81E3A" },
    candidateresigned: { bg: "#EAEAF2", color: "#65616F" },
};

/** Light-mode candidate status pill (kept local: the shared CandidateStatusBadge is also used by CandidateDetailPage's dark UI, which this page doesn't touch). */
const StatusPill = ({ status, t }) => {
    const key = status?.toLowerCase() ?? "initialized";
    const style = STATUS_STYLES[key] ?? STATUS_STYLES.initialized;
    return (
        // No nowrap: long status labels must wrap inside the 150px column.
        <Text as="span" display="inline-block" bg={style.bg} color={style.color} fontSize="11.5px" fontWeight={600} lineHeight={1.3} px={3} py={1} borderRadius="14px" maxW="full">
            {t(`candidateStatus.${key}`)}
        </Text>
    );
};

const formatPoints = (v) => (Number.isInteger(Number(v)) ? String(v) : Number(v).toFixed(1));

/** Light-mode score cell: number + colored progress bar, sharing the fixed score buckets with the rest of the app. */
const ScoreCell = ({ evaluation }) => {
    const { labels: scoreLabels } = useScoreLabels();
    const bucket = getScoreBucket(evaluation);
    const percent = evaluation?.scorePercent;
    const hasPoints = evaluation?.scoreRaw != null && evaluation?.scoreMax > 0;
    if (!bucket || percent == null) {
        return <Text fontSize="xs" color="#9A96A6" fontStyle="italic">{scoreLabels.noEvaluation}</Text>;
    }
    const clamped = Math.min(100, Math.max(0, percent));
    const solidColor = (bucket.color ?? "gray.400").replace(".400", ".600").replace(".500", ".600");
    return (
        <Box minW="96px">
            <HStack gap={1} align="baseline">
                {hasPoints ? (
                    <>
                        <Text fontSize="15px" fontWeight={800} color={solidColor}>{Math.round(clamped)}%</Text>
                        <Text fontSize="11px" color="#9A96A6">{formatPoints(evaluation.scoreRaw)}/{formatPoints(evaluation.scoreMax)} pkt</Text>
                    </>
                ) : (
                    <Text fontSize="15px" fontWeight={800} color={solidColor}>{Math.round(clamped)}%</Text>
                )}
            </HStack>
            <Box w="96px" h="5px" borderRadius="999px" bg="#E7E4F0" mt={1} overflow="hidden">
                <Box w={`${Math.round(clamped)}%`} h="full" bg={bucket.color} borderRadius="999px" />
            </Box>
            {bucket.dominant && (
                <Text fontSize="10px" fontWeight={600} color={solidColor} mt={0.5}>{scoreLabels[bucket.labelKey]}</Text>
            )}
        </Box>
    );
};

/**
 * Comment-bubble icon shown next to the row's "..." menu when a candidate has a note.
 * Opens on hover (with a short close delay so moving the cursor from the icon into the
 * popover doesn't flicker-close it) and on click, for touch devices.
 */
const LastNotePopover = ({ note, t, onGoToNotes }) => {
    const [open, setOpen] = useState(false);
    const closeTimer = useRef(null);

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(false), 150);
    };

    return (
        <Popover.Root open={open} onOpenChange={(e) => setOpen(e.open)} positioning={{ placement: 'bottom-end' }}>
            <Popover.Trigger asChild>
                <IconButton
                    variant="ghost"
                    size="sm"
                    color="#9A96A6"
                    aria-label={t("candidatePage.lastNoteContent")}
                    _hover={{ bg: "#F6F6FB", color: "#6D28D9" }}
                    onMouseEnter={() => { cancelClose(); setOpen(true); }}
                    onMouseLeave={scheduleClose}
                    onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                >
                    <HiOutlineChatBubbleLeft />
                </IconButton>
            </Popover.Trigger>
            <Portal>
                <Popover.Positioner>
                    <Popover.Content
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                        bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="12px" p={4} minW="280px" maxW="360px" boxShadow="0 8px 28px rgba(23,21,31,0.16)"
                    >
                        <VStack align="flex-start" gap={3}>
                            <VStack align="flex-start" gap={1} w="full">
                                <Text color="#9A96A6" fontSize="xs" fontWeight={600} textTransform="uppercase">
                                    {t("candidatePage.lastNoteContent")}
                                </Text>
                                <Text color="#17151F" fontSize="sm">{note.content}</Text>
                            </VStack>
                            <HStack justify="space-between" w="full" gap={4}>
                                <VStack align="flex-start" gap={0}>
                                    <Text color="#9A96A6" fontSize="xs">{t("candidatePage.lastNoteAuthor")}</Text>
                                    <Text color="#17151F" fontSize="xs">{note.authorFullName}</Text>
                                </VStack>
                                <VStack align="flex-start" gap={0}>
                                    <Text color="#9A96A6" fontSize="xs">{t("candidatePage.lastNoteDate")}</Text>
                                    <Text color="#17151F" fontSize="xs">{dayjs(note.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                                </VStack>
                            </HStack>
                            <HStack justify="space-between" w="full">
                                <Text color="#9A96A6" fontSize="xs">
                                    {t("candidatePage.lastNoteTotal", { count: note.totalNotes })}
                                </Text>
                                <Box
                                    as="button" type="button"
                                    fontSize="12px" px={2} py={1} borderRadius="6px" border="1px solid" borderColor="#D8D3E8" color="#65616F"
                                    onClick={(e) => { e.stopPropagation(); onGoToNotes(); }}
                                >
                                    {t("candidatePage.goToNotes")}
                                </Box>
                            </HStack>
                        </VStack>
                    </Popover.Content>
                </Popover.Positioner>
            </Portal>
        </Popover.Root>
    );
};

const RecruitmentInfoSkeleton = () => (
    <Box w="full" bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="16px" p={5}>
        <HStack justify="space-between" align="flex-start" mb={4}>
            <Skeleton height="22px" width="220px" borderRadius={6} />
            <Skeleton height="36px" width="110px" borderRadius="9px" />
        </HStack>
        <HStack gap={8} flexWrap="wrap">
            {Array.from({ length: 5 }).map((_, i) => (
                <VStack key={i} align="flex-start" gap={2}>
                    <Skeleton height="10px" width="70px" borderRadius={4} />
                    <Skeleton height="16px" width="110px" borderRadius={4} />
                </VStack>
            ))}
        </HStack>
    </Box>
);

const CandidateRowSkeleton = () => (
    <HStack px={5} gap={4} minH="36px" align="center">
        <SkeletonCircle size="18px" />
        <SkeletonCircle size="18px" />
        <HStack flex="2" gap={3} minW={0}>
            <SkeletonCircle size="36px" flexShrink={0} />
            <VStack align="flex-start" gap={2} flex={1} minW={0}>
                <Skeleton height="14px" width="55%" borderRadius={4} />
                <Skeleton height="10px" width="75%" borderRadius={4} />
            </VStack>
        </HStack>
        <Skeleton flex="1" height="22px" maxW="90px" borderRadius="999px" />
        <Skeleton flex="1" height="12px" maxW="70px" borderRadius={4} />
        <Skeleton flex="1" height="12px" maxW="110px" borderRadius={4} />
        <Skeleton flex="1" height="12px" maxW="110px" borderRadius={4} />
        <HStack gap={1} flexShrink={0}>
            <SkeletonCircle size="18px" />
        </HStack>
    </HStack>
);

const CandidatesListSkeleton = ({ rows = 6 }) => (
    <VStack w="full" gap={2} align="stretch">
        {Array.from({ length: rows }).map((_, i) => (
            <Box key={i} bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="13px" px={0} py={3}>
                <CandidateRowSkeleton />
            </Box>
        ))}
    </VStack>
);

const RecruitmentCandidatesPage = () => {
    const { t: tJobOffer } = useTranslation("jobOffer");
    const { t, i18n } = useTranslation("models");
    const { id, recruitmentId } = useParams();
    const navigate = useNavigate();

    const [recruitment, setRecruitment] = useState(null);
    useOfferPreparationPolling(recruitment ? [recruitment] : [], (_, patch) =>
        setRecruitment((prev) => (prev ? { ...prev, ...patch } : prev)));
    const scoringActive = useRecruitmentScoring(recruitment?.scoringEnabled);
    const [editModal, setEditModal] = useState(null);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [isRecruitmentLoading, setIsRecruitmentLoading] = useState(true);
    const [isCandidatesLoading, setIsCandidatesLoading] = useState(true);
    const [isXlsxLoading, setIsXlsxLoading] = useState(false);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [candFavLoadingId, setCandFavLoadingId] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [statusFilter, setStatusFilter] = useState([]);
    const [axisFilters, setAxisFilters] = useState({ process: [], aiRecommendation: [] });
    const axesParam = {
        processStatuses: axisFilters.process,
        aiRecommendations: axisFilters.aiRecommendation,
    };
    const [sortBy, setSortBy] = useState("createdat");
    const [sortDir, setSortDir] = useState("desc");
    const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [scoreMin, setScoreMin] = useState("");
    const [scoreMax, setScoreMax] = useState("");
    const [disqualifiedOnly, setDisqualifiedOnly] = useState(false);
    const [noScoreOnly, setNoScoreOnly] = useState(false);
    const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
    const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
    const [isBulkStatusLoading, setIsBulkStatusLoading] = useState(false);
    const [isBulkEmailLoading, setIsBulkEmailLoading] = useState(false);
    const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);
    const [candidatesRefreshKey, setCandidatesRefreshKey] = useState(0);
    const [mockCandidate, setMockCandidate] = useState(null);
    const [mockStep, setMockStep] = useState(0);
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const { openStatusModal, openEmailModal, openNoteModal, modals: singleActionModals } = useCandidateSingleActions({
        t,
        onStatusChanged: (candId, updated) =>
            setCandidates(prev => prev.map(c => c.id === candId ? { ...c, status: updated.status, processStatus: updated.processStatus, aiRecommendation: updated.aiRecommendation } : c)),
    });

    // Dev-only, client-side mock candidate for checking score colors.
    const MOCK_SCORE_SEQUENCE = [86, 74, 58, 42, 0, null];
    const handleAddMockCandidate = () => {
        if (!mockCandidate) {
            const scorePercent = MOCK_SCORE_SEQUENCE[0];
            setMockCandidate({
                id: "mock-preview",
                firstname: "Testowy",
                lastname: "Kandydat (mock)",
                status: "Created",
                isFavourite: false,
                email: "mock@example.com",
                phoneNumber: null,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                evaluation: scorePercent == null ? null : { scorePercent, status: "Scored" },
            });
            setMockStep(0);
            return;
        }
        const nextStep = (mockStep + 1) % MOCK_SCORE_SEQUENCE.length;
        const scorePercent = MOCK_SCORE_SEQUENCE[nextStep];
        setMockStep(nextStep);
        setMockCandidate((prev) => ({
            ...prev,
            evaluation: scorePercent == null ? null : { scorePercent, status: "Scored" },
        }));
    };
    const displayCandidates = mockCandidate ? [mockCandidate, ...candidates] : candidates;
    // Prev/next navigation in CandidateDetailPage.
    const navigableCandidateIds = candidates.map((c) => c.id);
    const candidateNavState = (candidateId) => ({
        candidateIds: navigableCandidateIds,
        candidateIndex: navigableCandidateIds.indexOf(candidateId),
    });

    const evaluationCollection = createListCollection({
        items: [
            { label: t("candidatePage.filterDisqualifiedOnly"), value: "disqualified" },
            { label: t("candidatePage.filterNoScore"), value: "noScore" },
        ],
    });
    const evaluationFilterValue = [
        ...(disqualifiedOnly ? ["disqualified"] : []),
        ...(noScoreOnly ? ["noScore"] : []),
    ];

    const axisCollections = Object.fromEntries(AXES.map(axis => [axis, createListCollection({
        items: {
            process: ["Contact", "Interview", "Verification", "Completed", "Resignation"],
            aiRecommendation: ["Positive", "Conditional", "Negative", "NoData"],
        }[axis].map(v => ({ label: t(`candidateAxes.${axis}.${v.toLowerCase()}`), value: v })),
    })]));

    const statusCollection = createListCollection({
        items: [
            { label: t("candidateStatus.initialized"), value: "Initialized" },
            { label: t("candidateStatus.created"), value: "Created" },
            { label: t("candidateStatus.finalized"), value: "Finalized" },
            { label: t("candidateStatus.qualifiedfornextstage"), value: "QualifiedForNextStage" },
            { label: t("candidateStatus.furtherselection"), value: "FurtherSelection" },
            { label: t("candidateStatus.readytohire"), value: "ReadyToHire" },
            { label: t("candidateStatus.hired"), value: "Hired" },
            { label: t("candidateStatus.rejected"), value: "Rejected" },
            { label: t("candidateStatus.candidateresigned"), value: "CandidateResigned" },
        ],
    });

    useSetPageHeader(t("candidatePage.headerTitle"), recruitment?.jobTitle ?? "", <HiOutlineUserGroup />);

    const handleEditRecruitment = async () => {
        setIsEditLoading(true);
        try {
            const data = await ModelService.getRecruitmentById(recruitmentId);
            setEditModal({ initialValues: fromApiResponse(data) });
        } finally {
            setIsEditLoading(false);
        }
    };

    const refreshRecruitment = () =>
        ConversationService.getRecruitmentById(id, recruitmentId).then(setRecruitment);

    useEffect(() => {
        setIsRecruitmentLoading(true);
        ConversationService.getRecruitmentById(id, recruitmentId)
            .then(setRecruitment)
            .finally(() => setIsRecruitmentLoading(false));
    }, [id, recruitmentId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setIsCandidatesLoading(true);
        const statuses = statusFilter;
        CandidateService.getByRecruitment(
            recruitmentId, page, pageSize, statuses,
            showFavoriteOnly || null, sortBy, sortDir, debouncedSearch || null,
            createdFrom || null, createdTo || null,
            scoreMin === "" ? null : Number(scoreMin), scoreMax === "" ? null : Number(scoreMax),
            disqualifiedOnly || null, noScoreOnly || null, axesParam
        )
            .then((data) => {
                setCandidates(data?.items ?? []);
                setTotalCount(data?.totalCount ?? 0);
                setSelectedIds(new Set());
            })
            .finally(() => setIsCandidatesLoading(false));
    }, [recruitmentId, page, pageSize, statusFilter, sortBy, sortDir, showFavoriteOnly, debouncedSearch, createdFrom, createdTo, scoreMin, scoreMax, disqualifiedOnly, noScoreOnly, axisFilters, candidatesRefreshKey]);

    const isAllSelected = candidates.length > 0 && selectedIds.size === candidates.length;
    const isIndeterminate = selectedIds.size > 0 && selectedIds.size < candidates.length;

    const toggleSelect = (candId) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(candId) ? next.delete(candId) : next.add(candId);
            return next;
        });
    };

    const toggleAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(candidates.map((c) => c.id)));
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("desc");
        }
        setPage(1);
    };

    const handleDownloadXlsx = async () => {
        setIsXlsxLoading(true);
        try {
            const ids = selectedIds.size > 0 ? Array.from(selectedIds) : [];
            const statuses = statusFilter;
            const response = await CandidateService.exportXlsx(
                recruitmentId, ids, statuses, showFavoriteOnly || null, sortBy, sortDir, debouncedSearch || null, axesParam
            );
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kandydaci_${recruitment.jobTitle.replace(' ', '_')}_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } finally {
            setIsXlsxLoading(false);
        }
    };

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await ModelService.toggleFavorite(recruitmentId);
        setRecruitment(updated);
        setIsFavLoading(false);
    };

    const handleCopyChatLink = () => {
        navigator.clipboard.writeText(`${window.location.origin}/c${recruitment.chatPath}`);
        toaster.create({ type: "success", title: "Skopiowano link do schowka" });
    };

    const handleToggleCandidateFavorite = async (e, candId) => {
        e.stopPropagation();
        if (candId === "mock-preview") {
            setMockCandidate((prev) => (prev ? { ...prev, isFavourite: !prev.isFavourite } : prev));
            return;
        }
        setCandFavLoadingId(candId);
        const updated = await CandidateService.toggleFavorite(candId);
        setCandidates((prev) =>
            prev.map((c) => c.id === candId ? { ...c, isFavourite: updated.isFavourite } : c)
        );
        setCandFavLoadingId(null);
    };

    const handleSelectAllFilter = async () => {
        setIsSelectAllLoading(true);
        try {
            const statuses = statusFilter;
            const data = await CandidateService.getByRecruitment(
                recruitmentId, 1, totalCount, statuses,
                showFavoriteOnly || null, sortBy, sortDir, debouncedSearch || null,
                createdFrom || null, createdTo || null,
                null, null, null, null, axesParam
            );
            setSelectedIds(new Set((data?.items ?? []).map(c => c.id)));
        } finally {
            setIsSelectAllLoading(false);
        }
    };

    const handleBulkChangeStatus = async (status, note) => {
        setIsBulkStatusLoading(true);
        try {
            const result = await CandidateService.bulkChangeStatus(Array.from(selectedIds), status, note);
            toaster.create({
                type: result.failureCount > 0 ? "warning" : "success",
                title: t("candidatePage.bulkChangeStatusSuccess", {
                    success: result.successCount,
                    total: result.successCount + result.failureCount,
                }),
            });
            setShowBulkStatusModal(false);
            setSelectedIds(new Set());
            setCandidatesRefreshKey(k => k + 1);
        } catch {
            toaster.create({ type: "error", title: t("candidatePage.bulkChangeStatusError") });
        } finally {
            setIsBulkStatusLoading(false);
        }
    };

    const handleBulkSendEmail = async (subject, body, files) => {
        setIsBulkEmailLoading(true);
        try {
            const result = await CandidateService.bulkSendEmail(Array.from(selectedIds), subject, body, files);
            toaster.create({
                type: result.failureCount > 0 ? "warning" : "success",
                title: t("candidatePage.bulkEmailSuccess", {
                    success: result.successCount,
                    total: result.successCount + result.failureCount,
                }),
            });
            setShowBulkEmailModal(false);
        } catch {
            toaster.create({ type: "error", title: t("candidatePage.bulkEmailError") });
        } finally {
            setIsBulkEmailLoading(false);
        }
    };

    const totalPages = Math.ceil(totalCount / pageSize);

    const pillFieldStyle = {
        display: "flex",
        alignItems: "center",
        gap: 2,
        bg: "white",
        border: "1px solid",
        borderColor: "#E7E4F0",
        borderRadius: "10px",
        px: 3,
        h: "38px",
        fontSize: "13px",
        color: "#65616F",
    };

    return (
        <>
        <VStack w="full" py={2} gap={5} align="flex-start">
            {!isRecruitmentLoading && recruitment?.scenario?.id && (
                <HStack
                    as="button"
                    type="button"
                    onClick={() => navigate(`/models/${id}/scenarios/${recruitment.scenario.id}`)}
                    color="#65616F"
                    fontSize="sm"
                    gap={1}
                    cursor="pointer"
                    _hover={{ color: "#17151F" }}
                >
                    <HiOutlineChevronLeft size={14} /> {t("candidatePage.backToRecruitmentsList")}
                </HStack>
            )}
            {isRecruitmentLoading ? (
                <RecruitmentInfoSkeleton />
            ) : recruitment && (
                <Box w="full" bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="16px" boxShadow="0 1px 2px rgba(23,21,31,0.04), 0 10px 24px -14px rgba(23,21,31,0.16)" p={5}>
                    <HStack justify="space-between" align="flex-start" mb={4} flexWrap="wrap" gap={2}>
                        <HStack gap={2}>
                            <Text fontWeight={700} fontSize="lg" color="#17151F">{recruitment.jobTitle}</Text>
                            <IconButton
                                variant="ghost"
                                size="sm"
                                aria-label="Ulubione"
                                color={recruitment?.isFavorite ? "#B7791F" : "#9A96A6"}
                                loading={isFavLoading}
                                onClick={handleToggleFavorite}
                                _hover={{ bg: "#F6F6FB" }}
                            >
                                <HiStar />
                            </IconButton>
                        </HStack>
                        <HStack gap={2} flexWrap="wrap">
                            {scoringActive && (
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handleAddMockCandidate}
                                    px={3} py={2} borderRadius="8px" fontSize="13px" color="#65616F"
                                    _hover={{ bg: "#F6F6FB" }}
                                >
                                    {mockCandidate ? "Testowy kandydat: kolejny wynik" : "Dodaj testowego kandydata"}
                                </Box>
                            )}
                            <Box
                                as="button"
                                type="button"
                                onClick={handleEditRecruitment}
                                disabled={isOfferPreparing(recruitment)}
                                display="flex" alignItems="center" gap={2}
                                bg="white" border="1px solid" borderColor="#D8D3E8" borderRadius="9px"
                                px={4} py={2} fontSize="13px" fontWeight={600} color="#17151F"
                                opacity={isEditLoading ? 0.6 : 1} cursor={isEditLoading ? "not-allowed" : "pointer"}
                                pointerEvents={isEditLoading ? "none" : "auto"}
                                _hover={{ bg: "#F6F6FB" }}
                            >
                                {isEditLoading && <Spinner size="xs" />}
                                {t("scenarioDetailPage.edit")}
                            </Box>
                            {recruitment.chatPath && !["draft", "inactive"].includes(recruitment.status?.toLowerCase()) && (
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handleCopyChatLink}
                                    disabled={isOfferLinkLocked(recruitment)}
                                    title={isOfferLinkLocked(recruitment) ? t(offerLinkLockedKey(recruitment)) : undefined}
                                    display="flex" alignItems="center" gap={2}
                                    bg="white" border="1px solid" borderColor="#D8D3E8" borderRadius="9px"
                                    px={4} py={2} fontSize="13px" fontWeight={600} color="#17151F"
                                    opacity={isOfferLinkLocked(recruitment) ? 0.4 : 1}
                                    cursor={isOfferLinkLocked(recruitment) ? "not-allowed" : "pointer"}
                                    _hover={isOfferLinkLocked(recruitment) ? undefined : { bg: "#F6F6FB" }}
                                >
                                    {t("recruitmentDetailPage.copyLink")}
                                </Box>
                            )}
                            <Box
                                as="button"
                                type="button"
                                onClick={handleDownloadXlsx}
                                display="flex" alignItems="center" gap={2}
                                bg="white" border="1px solid" borderColor="#D8D3E8" borderRadius="9px"
                                px={4} py={2} fontSize="13px" fontWeight={600} color="#17151F"
                                opacity={isXlsxLoading ? 0.6 : 1} cursor={isXlsxLoading ? "not-allowed" : "pointer"}
                                _hover={{ bg: "#F6F6FB" }}
                            >
                                {isXlsxLoading && <Spinner size="xs" />}
                                {selectedIds.size > 0
                                    ? t("candidatePage.downloadXlsxSelected", { count: selectedIds.size })
                                    : t("candidatePage.downloadXlsx")}
                            </Box>
                        </HStack>
                    </HStack>
                    <HStack gap={8} align="flex-start" flexWrap="wrap">
                        <InfoField label={t("recruitmentDetailPage.fieldPosition")}>
                            <Text color="#17151F" fontSize="sm" fontWeight={700}>
                                {recruitment.jobTitle}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldStatus")}>
                            {(isOfferPreparing(recruitment) || isOfferPreparationFailed(recruitment)) ? (
                                <RecruitmentStatusBadge
                                    status={recruitment.status}
                                    preparationStatus={recruitment.preparationStatus}
                                    preparationProgress={recruitment.preparationProgress}
                                />
                            ) : (
                            <Text
                                as="span"
                                bg={recruitment.status?.toLowerCase() === "active" ? "#E4F7EC" : "#EAEAF2"}
                                color={recruitment.status?.toLowerCase() === "active" ? "#178A4C" : "#65616F"}
                                fontSize="11.5px" fontWeight={600} px={3} py={1} borderRadius="999px"
                            >
                                {tJobOffer(`enums.OFFER_STATUS.${recruitment.status?.toLowerCase()}`, recruitment.status)}
                            </Text>
                            )}
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldCreatedAt")}>
                            <Text color="#17151F" fontSize="sm">
                                {new Date(recruitment.createdAtUtc).toLocaleDateString(i18n.language)}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldCompany")}>
                            <Text color="#17151F" fontSize="sm">
                                {recruitment.companyName}
                            </Text>
                        </InfoField>
                        <InfoField label={t("recruitmentDetailPage.fieldLocation")}>
                            <Text color="#17151F" fontSize="sm">
                                {recruitment.location}
                            </Text>
                        </InfoField>
                    </HStack>
                </Box>
            )}

            <VStack w="full" align="flex-start" gap={3} pt={4} pb={selectedIds.size > 0 ? 20 : 0}>
                <MobileFiltersPanel
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    statusFilter={statusFilter}
                    onStatusChange={(val) => { setStatusFilter(val); setPage(1); }}
                    statusCollection={statusCollection}
                    showFavoriteOnly={showFavoriteOnly}
                    onFavoriteToggle={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
                    createdFrom={createdFrom}
                    onCreatedFromChange={(val) => { setCreatedFrom(val); setPage(1); }}
                    createdTo={createdTo}
                    onCreatedToChange={(val) => { setCreatedTo(val); setPage(1); }}
                />

                <HStack w="full" justify="space-between" align="center" display={{ base: 'none', md: 'flex' }} flexWrap="wrap" rowGap={2}>
                    <Text fontWeight={700} fontSize="lg" color="#17151F">
                        {t("candidatePage.candidatesListTitle")}
                    </Text>
                    <HStack gap={2} flexWrap="wrap">
                        <Box {...pillFieldStyle} minW="220px">
                            <HiOutlineMagnifyingGlass color="#9A96A6" />
                            <Input
                                placeholder={t("recruitmentDetailPage.searchPlaceholder")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                variant="outline"
                                border="none"
                                bg="transparent"
                                h="auto"
                                minH="auto"
                                minW={0}
                                borderRadius={0}
                                px={0}
                                fontSize="13px"
                                color="#17151F"
                                _placeholder={{ color: "#9A96A6" }}
                                _focus={{ boxShadow: "none", outline: "none" }}
                                _focusVisible={{ boxShadow: "none", outline: "none" }}
                            />
                        </Box>

                        <Select.Root
                            collection={statusCollection}
                            value={statusFilter}
                            onValueChange={(e) => { setStatusFilter(e.value); setPage(1); }}
                            multiple
                            size="md"
                            w={'190px'}
                        >
                            <Select.HiddenSelect />
                            <Select.Control>
                                <Select.Trigger
                                    h="38px"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="#E7E4F0"
                                    borderRadius="10px"
                                    fontSize="13px"
                                    color="#17151F"
                                >
                                    <Select.ValueText color="#17151F" placeholder={t("candidateStatus.all")} />
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator color="#9A96A6" />
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal>
                                <Select.Positioner>
                                    <Select.Content bg="white" borderColor="#E7E4F0" boxShadow="0 8px 24px rgba(23,21,31,0.12)">
                                        {statusCollection.items.map((opt) => (
                                            <Select.Item item={opt} key={opt.value} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                                {opt.label}
                                                <Select.ItemIndicator />
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select.Positioner>
                            </Portal>
                        </Select.Root>

                        {AXES.map(axis => (
                            <Select.Root
                                key={axis}
                                collection={axisCollections[axis]}
                                value={axisFilters[axis]}
                                onValueChange={(e) => { setAxisFilters(prev => ({ ...prev, [axis]: e.value })); setPage(1); }}
                                multiple
                                size="md"
                                w="170px"
                            >
                                <Select.HiddenSelect />
                                <Select.Control>
                                    <Select.Trigger h="38px" bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="10px" fontSize="13px" color="#17151F">
                                        <Select.ValueText color="#17151F" placeholder={t(`candidateAxes.title.${axis}`)} />
                                    </Select.Trigger>
                                    <Select.IndicatorGroup>
                                        <Select.Indicator color="#9A96A6" />
                                    </Select.IndicatorGroup>
                                </Select.Control>
                                <Portal>
                                    <Select.Positioner>
                                        <Select.Content bg="white" borderColor="#E7E4F0" boxShadow="0 8px 24px rgba(23,21,31,0.12)">
                                            {axisCollections[axis].items.map((opt) => (
                                                <Select.Item item={opt} key={opt.value} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                                    {opt.label}
                                                    <Select.ItemIndicator />
                                                </Select.Item>
                                            ))}
                                        </Select.Content>
                                    </Select.Positioner>
                                </Portal>
                            </Select.Root>
                        ))}

                        <Box
                            as="button"
                            type="button"
                            onClick={() => setShowMoreFilters((v) => !v)}
                            {...pillFieldStyle}
                            fontWeight={600}
                            bg={showMoreFilters ? "#F1E8FE" : "white"}
                            color={showMoreFilters ? "#6D28D9" : "#65616F"}
                            borderColor={showMoreFilters ? "#E4D4FC" : "#E7E4F0"}
                        >
                            {t("recruitmentDetailPage.filtersButton")}
                            <HiChevronDown style={{ transform: showMoreFilters ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
                        </Box>
                    </HStack>
                </HStack>

                {showMoreFilters && (
                    <HStack
                        display={{ base: 'none', md: 'flex' }}
                        w="full" gap={2} flexWrap="wrap"
                        bg="#FBFAFE" border="1px solid" borderColor="#E7E4F0" borderRadius="12px" p={3}
                    >
                        <DateInput
                            placeholder={t("candidatePage.filterCreatedFrom")}
                            value={createdFrom}
                            onChange={(e) => { setCreatedFrom(e.target.value); setPage(1); }}
                            size="md" w="160px"
                            bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="10px"
                            color="#17151F" _placeholder={{ color: "#9A96A6" }}
                        />
                        <DateInput
                            placeholder={t("candidatePage.filterCreatedTo")}
                            value={createdTo}
                            onChange={(e) => { setCreatedTo(e.target.value); setPage(1); }}
                            size="md" w="160px"
                            bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="10px"
                            color="#17151F" _placeholder={{ color: "#9A96A6" }}
                        />
                        {scoringActive && (
                            <>
                                <HStack
                                    gap={2}
                                    px={3}
                                    h="38px"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="#E7E4F0"
                                    borderRadius="10px"
                                    title={t("candidatePage.filterScoreHint")}
                                >
                                    <Text fontSize="13px" color="#6B6880" whiteSpace="nowrap">{t("candidatePage.filterScoreLabel")}</Text>
                                    <Input
                                        aria-label={t("candidatePage.filterScoreMin")}
                                        placeholder={t("candidatePage.filterScoreFrom")}
                                        value={scoreMin}
                                        onChange={(e) => { setScoreMin(e.target.value); setPage(1); }}
                                        type="number" min="0" max="100" size="sm" w="56px" px={2}
                                        border="1px solid" borderColor="#E7E4F0" borderRadius="8px"
                                        color="#17151F" _placeholder={{ color: "#9A96A6" }}
                                    />
                                    <Text fontSize="13px" color="#6B6880">–</Text>
                                    <Input
                                        aria-label={t("candidatePage.filterScoreMax")}
                                        placeholder={t("candidatePage.filterScoreTo")}
                                        value={scoreMax}
                                        onChange={(e) => { setScoreMax(e.target.value); setPage(1); }}
                                        type="number" min="0" max="100" size="sm" w="56px" px={2}
                                        border="1px solid" borderColor="#E7E4F0" borderRadius="8px"
                                        color="#17151F" _placeholder={{ color: "#9A96A6" }}
                                    />
                                    <Text fontSize="13px" color="#6B6880">%</Text>
                                </HStack>
                                <Select.Root
                                    collection={evaluationCollection}
                                    value={evaluationFilterValue}
                                    onValueChange={(e) => {
                                        setDisqualifiedOnly(e.value.includes("disqualified"));
                                        setNoScoreOnly(e.value.includes("noScore"));
                                        setPage(1);
                                    }}
                                    multiple
                                    size="md"
                                    w={'190px'}
                                >
                                    <Select.HiddenSelect />
                                    <Select.Control>
                                        <Select.Trigger
                                            h="38px"
                                            bg="white"
                                            border="1px solid"
                                            borderColor="#E7E4F0"
                                            borderRadius="10px"
                                            fontSize="13px"
                                            color="#17151F"
                                        >
                                            <Select.ValueText color="#17151F" placeholder={t("candidatePage.filterEvaluationPlaceholder")} />
                                        </Select.Trigger>
                                        <Select.IndicatorGroup>
                                            <Select.Indicator color="#9A96A6" />
                                        </Select.IndicatorGroup>
                                    </Select.Control>
                                    <Portal>
                                        <Select.Positioner>
                                            <Select.Content bg="white" borderColor="#E7E4F0" boxShadow="0 8px 24px rgba(23,21,31,0.12)">
                                                {evaluationCollection.items.map((opt) => (
                                                    <Select.Item item={opt} key={opt.value} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                                        {opt.label}
                                                        <Select.ItemIndicator />
                                                    </Select.Item>
                                                ))}
                                            </Select.Content>
                                        </Select.Positioner>
                                    </Portal>
                                </Select.Root>
                            </>
                        )}
                    </HStack>
                )}

                {selectedIds.size > 0 && (
                    <BulkActionsBar
                        selectedCount={selectedIds.size}
                        totalCount={totalCount}
                        isAllSelected={isAllSelected}
                        totalPages={totalPages}
                        onSelectAll={handleSelectAllFilter}
                        onDeselectAll={() => setSelectedIds(new Set())}
                        onChangeStatus={() => setShowBulkStatusModal(true)}
                        onSendEmail={() => setShowBulkEmailModal(true)}
                        onExport={handleDownloadXlsx}
                        isXlsxLoading={isXlsxLoading}
                        isSelectAllLoading={isSelectAllLoading}
                    />
                )}

                <Box w="full" position="relative">
                    {/* Refetch keeps the list visible (dimmed) under a spinner. */}
                    {isCandidatesLoading && candidates.length > 0 && (
                        <VStack
                            position="absolute" inset={0} zIndex={1} align="center" justify="center"
                            bg="rgba(246,246,251,0.7)" borderRadius="12px" minH={100}
                        >
                            <Spinner color="purple.500" size="lg" />
                        </VStack>
                    )}

                    {isCandidatesLoading && candidates.length === 0 ? (
                        <CandidatesListSkeleton />
                    ) : (
                    <>
                    {/* Desktop list */}
                    <VStack
                        display={{ base: 'none', md: 'flex' }}
                        w="full" gap={2} align="stretch"
                        opacity={isCandidatesLoading ? 0.5 : 1}
                        transition="opacity 0.15s"
                    >
                        {displayCandidates.length === 0 && !isCandidatesLoading && (
                            <Box w="full" bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="14px" py={10} textAlign="center">
                                <Text color="#9A96A6" fontSize="sm">{t("candidatePage.noCandidates")}</Text>
                            </Box>
                        )}

                        {displayCandidates.length > 0 && (
                            <HStack px={5} gap={4} color="#9A96A6" fontSize="11px" fontWeight={700} textTransform="uppercase" letterSpacing="wider">
                                {/* Invisible copies keep header aligned with row controls. */}
                                <Box visibility="hidden" flexShrink={0}>
                                    <Checkbox.Root aria-hidden tabIndex={-1}>
                                        <Checkbox.HiddenInput />
                                        <Checkbox.Control>
                                            <Checkbox.Indicator />
                                        </Checkbox.Control>
                                    </Checkbox.Root>
                                </Box>
                                <IconButton
                                    variant="ghost"
                                    size="sm"
                                    flexShrink={0}
                                    aria-label={t("candidatePage.showFavouritesOnly")}
                                    title={t("candidatePage.showFavouritesOnly")}
                                    color={showFavoriteOnly ? "#B7791F" : "#9A96A6"}
                                    onClick={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
                                    _hover={{ bg: "#F6F6FB" }}
                                >
                                    {showFavoriteOnly ? <HiStar /> : <HiOutlineStar />}
                                </IconButton>
                                <Box flex="2">{t("candidatePage.columnFullName")}</Box>
                                <Box flex="1">{t("candidatePage.columnStatus")}</Box>
                                {scoringActive && (
                                    <HStack flex="1" gap={1} cursor="pointer" onClick={() => handleSort("score")} _hover={{ color: "#65616F" }}>
                                        <Text>{t("candidatePage.columnScore")}</Text>
                                        {sortBy === "score" && (sortDir === "asc" ? <HiArrowUp size={11} /> : <HiArrowDown size={11} />)}
                                    </HStack>
                                )}
                                <Box flex="1">{t("candidatePage.columnLanguage")}</Box>
                                <HStack flex="1" gap={1} cursor="pointer" onClick={() => handleSort("createdat")} _hover={{ color: "#65616F" }}>
                                    <Text>{t("candidatePage.columnCreatedAt")}</Text>
                                    {sortBy === "createdat" && (sortDir === "asc" ? <HiArrowUp size={11} /> : <HiArrowDown size={11} />)}
                                </HStack>
                                <HStack flex="1" gap={1} cursor="pointer" onClick={() => handleSort("updatedat")} _hover={{ color: "#65616F" }}>
                                    <Text>{t("candidatePage.columnLastActivity")}</Text>
                                    {sortBy === "updatedat" && (sortDir === "asc" ? <HiArrowUp size={11} /> : <HiArrowDown size={11} />)}
                                </HStack>
                                <HStack gap={1} visibility="hidden" flexShrink={0}>
                                    <IconButton variant="ghost" size="sm" aria-hidden tabIndex={-1}><HiOutlineChatBubbleLeft /></IconButton>
                                    <IconButton variant="ghost" size="sm" aria-hidden tabIndex={-1}><HiEllipsisVertical /></IconButton>
                                </HStack>
                            </HStack>
                        )}

                        {displayCandidates.map((cand) => {
                            const lastEvent = cand.lastTimelineEvent ?? null;
                            const lastEventBadge = lastEvent ? getTimelineBadgeProps(lastEvent.kind, t) : null;
                            const lastEventMeta = lastEvent ? parseMetadata(lastEvent.metadata) : null;
                            const lastEventDescription = lastEvent ? getTimelineEventDescription(lastEvent, t) : null;
                            const lastEventMetaDisplay = lastEvent ? formatMetadataDisplay(lastEvent, t) : null;
                            const lastEventNoteDisplay = lastEvent && (lastEvent.kind || '').toLowerCase() === 'statuschange'
                                ? (lastEventMeta?.note ?? lastEventMeta?.Note ?? null) : null;
                            const fullName = [cand.firstname, cand.lastname].filter(Boolean).join(" ") || "-";
                            const avatarColors = avatarColorsFor(cand.id ?? fullName);

                            return (
                                <Box
                                    key={cand.id}
                                    bg="white"
                                    border="1px solid"
                                    borderColor="#E7E4F0"
                                    borderRadius="13px"
                                    px={5}
                                    py={3}
                                    cursor="pointer"
                                    transition="border-color 0.15s, box-shadow 0.15s"
                                    _hover={{ borderColor: "#D8D3E8", boxShadow: "0 1px 2px rgba(23,21,31,0.04), 0 10px 24px -14px rgba(23,21,31,0.16)" }}
                                    onClick={() =>
                                        cand.id !== "mock-preview" &&
                                        navigate(`/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`, {
                                            state: candidateNavState(cand.id),
                                        })
                                    }
                                >
                                    <HStack gap={4} align="center" minH="36px">
                                        <Box onClick={(e) => e.stopPropagation()}>
                                            <Checkbox.Root
                                                checked={selectedIds.has(cand.id)}
                                                onCheckedChange={() => toggleSelect(cand.id)}
                                                disabled={cand.id === "mock-preview"}
                                                aria-label="Select candidate"
                                            >
                                                <Checkbox.HiddenInput />
                                                <Checkbox.Control borderColor="#D8D3E8" _checked={{ bg: "purple.500", borderColor: "purple.500" }}>
                                                    <Checkbox.Indicator />
                                                </Checkbox.Control>
                                            </Checkbox.Root>
                                        </Box>

                                        <IconButton
                                            variant="ghost"
                                            size="sm"
                                            aria-label="Ulubione"
                                            color={cand.isFavourite ? "#B7791F" : "#C4C1CE"}
                                            loading={candFavLoadingId === cand.id}
                                            onClick={(e) => handleToggleCandidateFavorite(e, cand.id)}
                                            _hover={{ bg: "#F6F6FB" }}
                                        >
                                            {cand.isFavourite ? <HiStar /> : <HiOutlineStar />}
                                        </IconButton>

                                        <HStack flex="2" gap={3} minW={0}>
                                            <Box w="36px" h="36px" borderRadius="full" bg={avatarColors.bg} color={avatarColors.color}
                                                display="flex" alignItems="center" justifyContent="center" fontWeight={700} fontSize="12.5px" flexShrink={0}>
                                                {initialsOf(cand.firstname, cand.lastname)}
                                            </Box>
                                            <VStack align="flex-start" gap={0} minW={0} flex={1} minH="34px" justify="center">
                                                <Text color="#17151F" fontSize="14px" fontWeight={600} truncate w="full">{fullName}</Text>
                                                {(cand.email || cand.phoneNumber) && (
                                                    <HStack gap={2} fontSize="12px" color="#65616F" minW={0} w="full">
                                                        {cand.email && <Text truncate minW={0}>{cand.email}</Text>}
                                                        {cand.phoneNumber && (
                                                            <Link href={`tel:${cand.phoneNumber}`} color="#65616F" onClick={(e) => e.stopPropagation()} whiteSpace="nowrap" flexShrink={0}>
                                                                {cand.phoneNumber}
                                                            </Link>
                                                        )}
                                                    </HStack>
                                                )}
                                            </VStack>
                                        </HStack>

                                        <Box flex="1" onClick={(e) => e.stopPropagation()}>
                                            <Box as="button" type="button" cursor="pointer" outline="none" onClick={() => openStatusModal(cand.id, cand.status)}>
                                                <StatusPill status={cand.status} t={t} />
                                            </Box>
                                        </Box>

                                        {scoringActive && (
                                            <Box flex="1">
                                                <ScoreCell evaluation={toEvaluationBucketShape(cand.evaluation)} />
                                            </Box>
                                        )}

                                        <Box flex="1">
                                            <Text color="#65616F" fontSize="13px">{cand.language ?? "-"}</Text>
                                        </Box>

                                        <Box flex="1">
                                            <Text color="#65616F" fontSize="13px">{dayjs(cand.createdAt).format('DD.MM.YYYY HH:mm')}</Text>
                                        </Box>

                                        <Box flex="1" onClick={(e) => e.stopPropagation()}>
                                            {lastEvent ? (
                                                <Popover.Root positioning={{ placement: 'bottom-start' }}>
                                                    <Popover.Trigger asChild>
                                                        <Box as="button" type="button" cursor="pointer" textAlign="left" _hover={{ opacity: 0.75 }}>
                                                            <Text color="#65616F" fontSize="13px">{dayjs(cand.updatedAt).format('DD.MM.YYYY HH:mm')}</Text>
                                                        </Box>
                                                    </Popover.Trigger>
                                                    <Portal>
                                                        <Popover.Positioner>
                                                            <Popover.Content bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="12px" p={4} minW="280px" maxW="400px" boxShadow="0 8px 28px rgba(23,21,31,0.16)">
                                                                <VStack align="flex-start" gap={2}>
                                                                    <HStack justify="space-between" w="full" flexWrap="wrap" gap={1}>
                                                                        <HStack gap={2}>
                                                                            <Badge variant="subtle" colorPalette={lastEventBadge.colorPalette} borderRadius={6} fontSize="xs" fontWeight={600}>
                                                                                {lastEventBadge.label}
                                                                            </Badge>
                                                                            <Text color="#65616F" fontSize="xs">{lastEvent.authorFullName ?? "System"}</Text>
                                                                        </HStack>
                                                                        <Text color="#65616F" fontSize="xs">{dayjs(lastEvent.occurredAt).format("DD.MM.YYYY HH:mm")}</Text>
                                                                    </HStack>
                                                                    {lastEventDescription && <Text color="#17151F" fontSize="sm">{lastEventDescription}</Text>}
                                                                    {lastEventMetaDisplay && (
                                                                        <Box bg="#F6F6FB" borderRadius={8} px={3} py={2} w="full">
                                                                            <Text fontFamily="mono" fontSize="xs" color="#65616F">{lastEventMetaDisplay}</Text>
                                                                        </Box>
                                                                    )}
                                                                    {lastEventNoteDisplay && <Text color="#17151F" fontSize="sm" fontStyle="italic">Notatka: {lastEventNoteDisplay}</Text>}
                                                                    <Box
                                                                        as="button" type="button" alignSelf="flex-end"
                                                                        fontSize="12px" px={2} py={1} borderRadius="6px" border="1px solid" borderColor="#D8D3E8" color="#65616F"
                                                                        onClick={() => navigate(
                                                                            `/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`,
                                                                            { state: { tab: 'timeline', highlightId: lastEvent?.id ?? null } }
                                                                        )}
                                                                    >
                                                                        {t("candidatePage.goToTimeline")}
                                                                    </Box>
                                                                </VStack>
                                                            </Popover.Content>
                                                        </Popover.Positioner>
                                                    </Portal>
                                                </Popover.Root>
                                            ) : (
                                                <Text color="#65616F" fontSize="13px">{dayjs(cand.updatedAt).format('DD.MM.YYYY HH:mm')}</Text>
                                            )}
                                        </Box>

                                        <HStack gap={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
                                            {cand.lastNote ? (
                                                <LastNotePopover
                                                    note={cand.lastNote}
                                                    t={t}
                                                    onGoToNotes={() => navigate(
                                                        `/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`,
                                                        { state: { tab: 'notes', highlightId: cand.lastNote?.id ?? null } }
                                                    )}
                                                />
                                            ) : (
                                                // Invisible placeholder keeps column widths equal across rows.
                                                <IconButton variant="ghost" size="sm" visibility="hidden" aria-hidden tabIndex={-1}>
                                                    <HiOutlineChatBubbleLeft />
                                                </IconButton>
                                            )}

                                            <Menu.Root>
                                                <Menu.Trigger asChild>
                                                    <IconButton variant="ghost" size="sm" color="#9A96A6" aria-label={t("candidatePage.columnActions")} _hover={{ bg: "#F6F6FB" }}>
                                                        <HiEllipsisVertical />
                                                    </IconButton>
                                                </Menu.Trigger>
                                                <Portal>
                                                    <Menu.Positioner>
                                                        <Menu.Content bg="white" border="1px solid" borderColor="#E7E4F0" boxShadow="0 8px 24px rgba(23,21,31,0.12)">
                                                            <Menu.Item value="email" disabled={!cand.email} onClick={() => openEmailModal(cand.id)} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                                                {t("candidateDetailPage.sendMessage")}
                                                            </Menu.Item>
                                                            <Menu.Item value="status" onClick={() => openStatusModal(cand.id, cand.status)} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                                                {t("candidateDetailPage.changeStatus")}
                                                            </Menu.Item>
                                                            <Menu.Item value="note" onClick={() => openNoteModal(cand.id)} color="#17151F" _hover={{ bg: "#F6F6FB" }}>
                                                                {t("candidateDetailPage.addNote")}
                                                            </Menu.Item>
                                                        </Menu.Content>
                                                    </Menu.Positioner>
                                                </Portal>
                                            </Menu.Root>
                                        </HStack>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>

                    {/* Mobile card list */}
                    <VStack
                        display={{ base: 'flex', md: 'none' }}
                        w="full" gap={2}
                        opacity={isCandidatesLoading ? 0.5 : 1}
                        transition="opacity 0.15s"
                    >
                        {displayCandidates.length === 0 && !isCandidatesLoading && (
                            <Box w="full" bg="white" border="1px solid" borderColor="#E7E4F0" borderRadius="12px" p={6} textAlign="center">
                                <Text color="#9A96A6" fontSize="sm">{t("candidatePage.noCandidates")}</Text>
                            </Box>
                        )}
                        {displayCandidates.map((cand) => {
                            const fullName = [cand.firstname, cand.lastname].filter(Boolean).join(" ") || "-";
                            const avatarColors = avatarColorsFor(cand.id ?? fullName);
                            return (
                                <Box
                                    key={cand.id}
                                    w="full"
                                    bg="white"
                                    border="1px solid"
                                    borderColor="#E7E4F0"
                                    borderRadius="12px"
                                    p={3}
                                    cursor="pointer"
                                    onClick={() => cand.id !== "mock-preview" && navigate(`/models/${id}/recruitments/${recruitmentId}/candidates/${cand.id}`, {
                                        state: candidateNavState(cand.id),
                                    })}
                                    _active={{ opacity: 0.85 }}
                                >
                                    <HStack justify="space-between" align="flex-start">
                                        <HStack gap={2} flex={1} minW={0}>
                                            <IconButton
                                                variant="ghost" size="sm" aria-label="Ulubione"
                                                color={cand.isFavourite ? "#B7791F" : "#C4C1CE"}
                                                loading={candFavLoadingId === cand.id}
                                                flexShrink={0}
                                                onClick={(e) => handleToggleCandidateFavorite(e, cand.id)}
                                            >
                                                {cand.isFavourite ? <HiStar /> : <HiOutlineStar />}
                                            </IconButton>
                                            <Box w="34px" h="34px" borderRadius="full" bg={avatarColors.bg} color={avatarColors.color}
                                                display="flex" alignItems="center" justifyContent="center" fontWeight={700} fontSize="12px" flexShrink={0}>
                                                {initialsOf(cand.firstname, cand.lastname)}
                                            </Box>
                                            <VStack align="flex-start" gap={1} flex={1} minW={0}>
                                                <Text color="#17151F" fontSize="sm" fontWeight={600}>{fullName}</Text>
                                                {cand.email && <Text color="#65616F" fontSize="xs">{cand.email}</Text>}
                                                {cand.phoneNumber && (
                                                    <Link href={`tel:${cand.phoneNumber}`} color="#65616F" fontSize="xs" onClick={(e) => e.stopPropagation()}>
                                                        {cand.phoneNumber}
                                                    </Link>
                                                )}
                                                <Text color="#9A96A6" fontSize="xs" whiteSpace="nowrap">
                                                    {dayjs(cand.createdAt).format('DD.MM.YYYY HH:mm')}
                                                </Text>
                                            </VStack>
                                        </HStack>
                                        <VStack gap={1.5} align="flex-end" flexShrink={0} onClick={(e) => e.stopPropagation()}>
                                            <Box as="button" type="button" cursor="pointer" outline="none" onClick={() => openStatusModal(cand.id, cand.status)}>
                                                <StatusPill status={cand.status} t={t} />
                                            </Box>
                                            {scoringActive && <ScoreCell evaluation={toEvaluationBucketShape(cand.evaluation)} />}
                                        </VStack>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>
                    </>
                    )}
                </Box>

                {totalCount > 0 && (
                    <VStack w="full" gap={2} pt={1}>
                        <HStack justify="space-between" w="full" flexWrap="wrap" rowGap={2}>
                            <HStack gap={2}>
                                <Text color="#9A96A6" fontSize="sm" display={{ base: 'none', md: 'block' }}>
                                    {t("candidatePage.paginationRowsPerPage")}
                                </Text>
                                <select
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    style={{
                                        background: "white",
                                        border: "1px solid #E7E4F0",
                                        borderRadius: "8px",
                                        color: "#17151F",
                                        fontSize: "13px",
                                        padding: "5px 8px",
                                        cursor: "pointer",
                                    }}
                                >
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <Text color="#9A96A6" fontSize="sm">
                                    {t("candidatePage.paginationInfo", { page, totalPages, totalCount })}
                                </Text>
                            </HStack>
                            {totalPages > 1 && (
                                <HStack gap={2}>
                                    <Box
                                        as="button" type="button"
                                        onClick={() => setPage((p) => p - 1)}
                                        disabled={page <= 1}
                                        px={4} py={1.5} borderRadius="8px" border="1px solid" borderColor="#E7E4F0" bg="white"
                                        fontSize="13px" color="#65616F"
                                        opacity={page <= 1 ? 0.4 : 1} cursor={page <= 1 ? "not-allowed" : "pointer"}
                                        _hover={page <= 1 ? undefined : { bg: "#F6F6FB" }}
                                    >
                                        {t("candidatePage.paginationPrev")}
                                    </Box>
                                    <Box
                                        as="button" type="button"
                                        onClick={() => setPage((p) => p + 1)}
                                        disabled={page >= totalPages}
                                        px={4} py={1.5} borderRadius="8px" border="1px solid" borderColor="#E7E4F0" bg="white"
                                        fontSize="13px" color="#65616F"
                                        opacity={page >= totalPages ? 0.4 : 1} cursor={page >= totalPages ? "not-allowed" : "pointer"}
                                        _hover={page >= totalPages ? undefined : { bg: "#F6F6FB" }}
                                    >
                                        {t("candidatePage.paginationNext")}
                                    </Box>
                                </HStack>
                            )}
                        </HStack>
                    </VStack>
                )}
            </VStack>
        </VStack>
        <BulkChangeStatusModal
            isOpen={showBulkStatusModal}
            onClose={() => setShowBulkStatusModal(false)}
            count={selectedIds.size}
            onSubmit={handleBulkChangeStatus}
            isLoading={isBulkStatusLoading}
        />
        <BulkEmailModal
            isOpen={showBulkEmailModal}
            onClose={() => setShowBulkEmailModal(false)}
            count={selectedIds.size}
            onSubmit={handleBulkSendEmail}
            isLoading={isBulkEmailLoading}
        />
        {editModal && (
            <JobOfferFormModal
                onClose={() => setEditModal(null)}
                initialValues={editModal.initialValues}
                onSubmit={(values, isDraft) => {
                    if (isDraft) {
                        return ModelService.saveDraft({ ...values, scenarioId: values.scenarioId ?? editModal.initialValues?.scenarioId }).then(refreshRecruitment);
                    }
                    return ModelService.updateRecruitment(recruitmentId, values).then(() => {
                        refreshRecruitment();
                        setEditModal(null);
                    });
                }}
            />
        )}
        {singleActionModals}
        </>
    );
};

export default RecruitmentCandidatesPage;
